<template>
  <q-dialog
    v-model="shown"
  >
    <q-card>
      <q-card-section class="row items-center q-mx-xs">
        <div class="text-h6">
          Copy Booking Week
        </div>
        <q-space />
        <q-btn
          v-close-popup
          icon="close"
          flat
          round
          dense
        />
      </q-card-section>
      <q-card-section>
        <q-select
          v-model="selectedSiteId"
          :disable="bookingWeekCopyOptions && bookingWeekCopyOptions.loading"
          class="sensitive-input"
          option-value="id"
          emit-value
          label="Site To Copy From"
          map-options
          option-label="name"
          :options="currentUser.authorizedSiteOptions"
        />
      </q-card-section>
      <q-card-section>
        <q-select
          v-model="selectedCopyFromId"
          class="sensitive-input"
          option-value="id"
          emit-value
          label="Booking Week To Copy"
          map-options
          option-label="label"
          :options="bookingWeekOptions"
        />
      </q-card-section>
      <q-card-section>
        <q-expansion-item
          v-if="bookingWeekCopySummary.value"
          class="full-width"
          expand-separator
        >
          <template #header>
            <q-item-section avatar>
              <q-icon name="theaters" />
            </q-item-section>

            <q-item-section>
              <div class="text-h6">
                Movies to Import
              </div>
              <div>Found {{ bookingWeekCopySummary.value.movies_to_create.length }} Movies that do not exist for your site, these will be imported.</div>
            </q-item-section>
          </template>
          <q-list>
            <q-item
              v-for="movie in bookingWeekCopySummary.value.movies_to_create"
              :key="movie.name"
            >
              <q-item-section avatar>
                <img :src="movie.poster">
              </q-item-section>

              <q-item-section>
                <div class="text-h6">
                  {{ movie.name }}
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </q-expansion-item>
      </q-card-section>
      <q-card-section>
        <q-expansion-item
          v-if="bookingWeekCopySummary.value"
          class="full-width"
          expand-separator
        >
          <template #header>
            <q-item-section avatar>
              <q-icon name="schedule" />
            </q-item-section>

            <q-item-section>
              <div class="text-h6">
                Showings to Import
              </div>
              <div> Found {{ bookingWeekCopySummary.value.showings_to_create.length }} New Showings that do not exist yet for your site, these will be imported.</div>
            </q-item-section>
          </template>
          <q-card-section>
            <q-list>
              <q-item
                v-for="showings in showingsToCreate"
                :key="showings[0].movie"
                class="full-width"
              >
                <q-expansion-item
                  class="full-width"
                  expand-separator
                  :caption="showings.length + ' showings to create'"
                >
                  <template #header>
                    <q-item-section avatar>
                      <img :src="showings[0].poster">
                    </q-item-section>

                    <q-item-section>
                      <div class="text-h6">
                        {{ showings[0].movie }}
                      </div>
                      <div>{{ showings.length }} Showings to Create</div>
                    </q-item-section>
                  </template>
                  <q-card>
                    <q-card-section>
                      <div
                        v-for="screenShowings in $_.groupBy(showings, 'screen')"
                        :key="screenShowings[0].screen"
                        class="row justify-around q-mb-md"
                      >
                        <div class="col-6 text-h6">
                          {{ screenShowings[0].screen }}
                        </div>
                        <div class="col-6">
                          <div
                            v-for="showing in screenShowings"
                            :key="showing.time"
                            class="row"
                          >
                            <formatted-date
                              :value="showing.time"
                            />
                          </div>
                        </div>
                      </div>
                    </q-card-section>
                  </q-card>
                </q-expansion-item>
              </q-item>
            </q-list>
          </q-card-section>
        </q-expansion-item>
      </q-card-section>
      <q-card-actions class="q-mt-md q-mb-xs">
        <q-btn
          :label="copyButtonLabel"
          color="primary"
          :disable="loading || !showingCount"
          @click="() => copyBookingWeek()"
        />
      </q-card-actions>
      <q-inner-loading
        :showing="loading"
      />
    </q-card>
  </q-dialog>
</template>

<script>
import { resetSessionIdentifier } from 'src/models/model'
import FormattedDate from 'src/modules/admin/components/FormattedDate.vue'

export default {
  name: 'BookingWeekCopyDialog',
  components: {
    FormattedDate
  },
  props: {
    modelValue: {
      type: Boolean,
      required: false
    },
    bookingWeek: {
      type: Object,
      required: true
    }
  },
  context: ['currentUser'],
  emits: ['update:model-value'],
  data () {
    return {
      selectedSiteId: null,
      selectedCopyFromId: null,
      shown: false,
      loading: false
    }
  },
  computed: {
    bookingWeekOptions () {
      if (this.selectedSiteId && this.bookingWeekCopyOptions?.data) {
        return this.bookingWeekCopyOptions.value
      } else {
        return []
      }
    },
    showingsToCreate () {
      if (this.bookingWeekCopySummary?.value?.showings_to_create) {
        return _(this.bookingWeekCopySummary.value.showings_to_create).groupBy('movie').values().value()
      } else {
        return []
      }
    },
    copyButtonLabel () {
      if (this.bookingWeekCopySummary?.value?.showings_to_create) {
        return 'Copy (' + this.showingCount + ' showings)'
      } else {
        return 'Copy'
      }
    },
    showingCount () {
      return this.selectedSiteId ? this.bookingWeekCopySummary?.value?.showings_to_create.length : 0
    }
  },
  models: {
    bookingWeekCopySummary: {
      variables () {
        return {
          copyFrom: this.selectedCopyFromId,
          copyTo: this.bookingWeek.id,
          skip: !this.selectedCopyFromId
        }
      }
    },
    bookingWeekCopyOptions: {
      variables () {
        return {
          siteId: this.selectedSiteId,
          skip: !this.selectedSiteId,
          fromDate: this.bookingWeek?.date?.toString()
        }
      }
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    shown () {
      this.$emit('update:model-value', this.shown)
      this.bookingWeekCopyOptions?.reset()
    },
    'bookingWeekCopySummary.loading' () {
      this.loading = this.bookingWeekCopySummary.loading
    },
    selectedSiteId () {
      this.selectedCopyFromId = null
    },
    'bookingWeekCopyOptions.loading' () {
      if (this.selectedSiteId && !this.bookingWeekCopyOptions.loading) {
        const matchingDate = _.find(this.bookingWeekCopyOptions.value, option => option.date === this.bookingWeek.date.toString())

        if (matchingDate) {
          this.selectedCopyFromId = matchingDate.id
        }
      }
    },
    selectedCopyFromId () {
      if (!this.selectedCopyFromId) {
        this.bookingWeekCopySummary?.reset()
      }
    }
  },
  methods: {
    show () {
      this.shown = true
      this.selectedCopyFromId = null
      this.selectedSiteId = null
    },
    async copyBookingWeek () {
      if (!this.showingCount) {
        this.$mh.showErrors(['No showings to copy'])
      }
      this.loading = true
      this.bookingWeek.copyFromId = this.selectedCopyFromId
      const result = await this.bookingWeek.save()
      if (result.errors?.length) {
        this.$mh.showErrors(result.errors)
      } else {
        resetSessionIdentifier()
      }
      this.bookingWeek.copyFromId = null
      this.shown = false
      this.loading = false
    }
  }
}

</script>
